import type { CircadianAttack, EclipseEffect } from '../../../data/full/circadien';
import { unitFromAngle } from '../../../shared/angle';
import { clamp } from '../../../shared/scalar';
import type { Vec2 } from '../../../shared/vec';
import { applyEnemyStatus } from '../../enemy-status';
import { hurtEnemy, keepWhere, touches } from '../../effects';
import type { SkillModule } from '../../skills/types';
import type { EnemyState, PlayerState, PropState } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { flipClock } from '../clock';
import { ownerOf } from '../../props';

export const ECLIPSE = 'eclipse';

export const eclipse: SkillModule<EclipseEffect> = {
  cast(ctx, player, effect, _input, power) {
    const { state, tempo } = ctx;
    const distance = aimedAt(state.enemies, player, effect) ?? effect.reach;
    const { width, height } = state.arena;
    const x = clamp(player.x + player.aim.x * distance, 0, width);
    const y = clamp(player.y + player.aim.y * distance, 0, height);
    const shadow: PropState = {
      id: state.nextEntityId,
      kind: ECLIPSE,
      playerId: player.id,
      phase: 'flying',
      x,
      y,
      prevX: x,
      prevY: y,
      vx: 0,
      vy: 0,
      damage: effect.damage * power,
      ticksLeft: effect.beats * tempo.ticksPerBeat,
      hitsLeft: 0,
    };
    state.nextEntityId += 1;
    (state.props ??= []).push(shadow);
  },

  update(ctx) {
    const { state } = ctx;
    const props = state.props;
    if (props === undefined) {
      return;
    }
    let gone = false;
    for (const prop of props) {
      if (prop.kind !== ECLIPSE) {
        continue;
      }
      prop.ticksLeft -= 1;
      if (prop.ticksLeft > 0) {
        continue;
      }
      if (prop.phase === 'flying') {
        cover(ctx, prop);
      } else {
        gone = true;
      }
    }
    if (gone) {
      keepWhere(props, (prop) => prop.kind !== ECLIPSE || prop.ticksLeft > 0);
      if (props.length === 0) {
        delete state.props;
      }
    }
  },
};

export function eclipseRadius(
  player: Pick<PlayerState, 'modifiers' | 'suppressedTicks' | 'boosts'>,
  effect: Pick<EclipseEffect, 'radius'>,
): number {
  return statValue(player, 'skillRadiusMul', effect.radius);
}

// The shadow hurts what it covers, the rays what they cross beyond it: none takes both.
function cover(ctx: StepContext, prop: PropState): void {
  const { state, tempo } = ctx;
  const owner = ownerOf(state, prop);
  const effect = ctx.content.classes.get(owner.classId)?.skill.effect;
  if (effect?.kind !== 'eclipse') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" has no eclipse`);
  }
  const radius = eclipseRadius(owner, effect);
  const slowTicks = effect.slowBeats * tempo.ticksPerBeat;
  for (const enemy of state.enemies) {
    if (enemy.hp > 0 && touches(enemy, prop, radius)) {
      hurtEnemy(state, enemy, prop.damage, owner.id, prop);
      applyEnemyStatus(state, enemy, 'slowed', slowTicks);
    }
  }
  shine(ctx, prop, effect, radius);
  flipClock(owner, state.tick, tempo, circadianOf(ctx, owner).halfBars);
  state.events.push({ type: 'eclipseBurst', playerId: owner.id, x: prop.x, y: prop.y });
  prop.phase = 'grounded';
  prop.ticksLeft = slowTicks;
}

function shine(ctx: StepContext, prop: PropState, effect: EclipseEffect, radius: number): void {
  const { state } = ctx;
  const { rays } = effect;
  const power = prop.damage / effect.damage;
  const damage = power * rays.damage;
  const half = rays.width / 2;
  for (const way of eclipseRayWays(rays.count)) {
    for (const enemy of state.enemies) {
      if (enemy.hp <= 0 || touches(enemy, prop, radius)) {
        continue;
      }
      const dx = enemy.x - prop.x;
      const dy = enemy.y - prop.y;
      const along = dx * way.x + dy * way.y;
      const across = Math.abs(dy * way.x - dx * way.y);
      if (along >= 0 && along <= rays.length + enemy.radius && across <= half + enemy.radius) {
        hurtEnemy(state, enemy, damage, prop.playerId, prop);
      }
    }
  }
}

export function eclipseRayWays(count: number): Vec2[] {
  return Array.from({ length: count }, (_, rank) => unitFromAngle((2 * Math.PI * rank) / count));
}

function circadianOf(ctx: Pick<StepContext, 'content'>, player: PlayerState): CircadianAttack {
  const attack = ctx.content.classes.get(player.classId)?.attack;
  if (attack?.kind !== 'circadian') {
    throw new Error(`player ${String(player.id)} of class "${player.classId}" has no clock`);
  }
  return attack;
}

function aimedAt(
  enemies: readonly EnemyState[],
  player: PlayerState,
  effect: Pick<EclipseEffect, 'reach' | 'radius'>,
): number | undefined {
  let nearest: number | undefined;
  for (const enemy of enemies) {
    const dx = enemy.x - player.x;
    const dy = enemy.y - player.y;
    const along = dx * player.aim.x + dy * player.aim.y;
    const across = Math.abs(dy * player.aim.x - dx * player.aim.y);
    if (
      enemy.hp > 0 &&
      along > 0 &&
      along <= effect.reach &&
      across <= enemy.radius + effect.radius / 2 &&
      (nearest === undefined || along < nearest)
    ) {
      nearest = along;
    }
  }
  return nearest;
}
